import type { HalfDayMap, HalfDaySlot } from '../components/employee/HalfDayPicker'

/** Float slop for comparing fractional day sums (0.5 steps) against a balance —
 *  `0.5 + 1.0` must read as ≤ 1.5, not `1.5000000001 > 1.5`. */
export const DAY_EPS = 1e-9

/** Days a working day consumes under the current per-date selection: a picked
 *  AM/PM half costs 0.5, everything else (absent key ⇒ FULL) costs a whole. */
export function dayCost(slot: HalfDaySlot | undefined): number {
  return slot === 'AM' || slot === 'PM' ? 0.5 : 1
}

/** Total days the current per-date selection consumes across `workingDays`. */
export function selectionCost(
  workingDays: ReadonlyArray<string>,
  selection: HalfDayMap,
): number {
  return workingDays.reduce((sum, d) => sum + dayCost(selection[d]), 0)
}

/**
 * Working days whose FULL option must be disabled given the CURRENT selection.
 *
 * A day can't be taken full if doing so would push the total past `available` —
 * i.e. the OTHER days already consume more than `available - 1`. This is
 * dynamic, not positional: with 1.5 days left over a 2-day span, making day 1 a
 * half frees day 2 to be full (0.5 + 1.0 = 1.5), where a floor()-and-slice rule
 * would hard-lock the trailing day regardless of earlier picks.
 */
export function fullDisabledDates(
  workingDays: ReadonlyArray<string>,
  selection: HalfDayMap,
  available: number,
): string[] {
  if (!Number.isFinite(available) || available <= 0) return []
  return workingDays.filter((day) => {
    const othersCost = workingDays.reduce(
      (sum, other) => (other === day ? sum : sum + dayCost(selection[other])),
      0,
    )
    // FULL costs a whole day; disable it when the rest already leave < 1 free.
    return othersCost + 1 > available + DAY_EPS
  })
}

/**
 * If the current selection exceeds `available`, force trailing FULL days to AM
 * until the total fits, and return the corrected map. A selection already
 * within budget is returned unchanged (same reference), so this only ever ADDS
 * halves — it never fights a user who legitimately flips a seeded half to full.
 */
export function seedToFit(
  workingDays: ReadonlyArray<string>,
  selection: HalfDayMap,
  available: number,
): HalfDayMap {
  if (!Number.isFinite(available) || available <= 0) return selection
  let total = selectionCost(workingDays, selection)
  if (total <= available + DAY_EPS) return selection
  const next: Record<string, HalfDaySlot> = { ...selection }
  let changed = false
  for (let i = workingDays.length - 1; i >= 0 && total > available + DAY_EPS; i--) {
    const d = workingDays[i]
    if (d !== undefined && next[d] !== 'AM' && next[d] !== 'PM') {
      next[d] = 'AM'
      total -= 0.5
      changed = true
    }
  }
  return changed ? next : selection
}
